import { z } from 'zod';

const Text = z.string().min(1);
const TalkReferenceSchema = z.object({
  talk_id: Text,
  speaker: Text,
  prominence_score: z.number().optional(),
  usage_context: z.string().optional(),
});

export const CrossTalkSchema = z.object({
  conference_narrative: z.object({
    overall_message: Text,
    dominant_themes: z.array(Text),
    emotional_arc: Text,
    unique_emphases: z.array(Text),
  }),
  shared_themes: z.array(z.object({
    theme_name: Text,
    talks: z.array(TalkReferenceSchema),
    conference_prominence: z.number(),
    synthesis: Text,
  })),
  scripture_chains: z.array(z.object({
    reference: Text,
    talks: z.array(TalkReferenceSchema),
    significance: Text,
  })),
  speaker_connections: z.array(z.unknown()),
  thematic_clusters: z.array(z.unknown()),
});

export type CrossTalk = z.infer<typeof CrossTalkSchema>;

export async function loadCrossTalk(conferenceId: string): Promise<CrossTalk> {
  const response = await fetch(
    `/data/conference/${encodeURIComponent(conferenceId)}/cross-talk.json`,
  );
  if (!response.ok) throw new Error('Conference connections are unavailable.');
  return CrossTalkSchema.parse(await response.json());
}

function slug(value: string): string {
  return value
    .normalize('NFKD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
}

const CANONICAL_THEME_IDS: Record<string, string> = {
  'Creation and Plan of Salvation / Divine Identity':
    'theme-creation-and-plan-of-salvation',
  'Fasting and the Law of the Fast / Tithing':
    'theme-fasting-and-the-law-of-the-fast',
};

export function themeThreadId(themeName: string): string {
  return CANONICAL_THEME_IDS[themeName] ?? `theme-${slug(themeName)}`;
}

const SCRIPTURE_BOOK_IDS: Record<string, string> = {
  Alma: '213',
  Mosiah: '212',
  Matthew: '140',
  Ether: '218',
  '3 Nephi': '215',
  'Doctrine and Covenants': '302',
  Proverbs: '120',
  Malachi: '139',
  '2 Nephi': '206',
  Isaiah: '123',
};

function scriptureHref(reference: string): string {
  const first = reference.split(' / ')[0] ?? reference;
  const match = /^(.+?) (\d+):(\d+(?:-\d+)?)/.exec(first);
  if (!match) return '/threads';
  const [, book, chapter, verses] = match;
  const bookId = SCRIPTURE_BOOK_IDS[book];
  return bookId
    ? `/scripture/${bookId}/${chapter}/${verses}`
    : '/threads';
}

function link(href: string, label: string): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.dataset.router = '';
  anchor.textContent = label;
  return anchor;
}

export function createConferenceConnections(payload: CrossTalk): HTMLElement {
  // speaker_connections and thematic_clusters are intentionally deferred;
  // see docs/ARCHITECTURE.md for the reading-first UI scope decision.
  const page = document.createElement('div');
  page.className = 'conference-connections page-width';
  page.dataset.route = 'explore-view';
  page.dataset.exploreView = 'conference-connections';
  const header = document.createElement('header');
  header.className = 'explore-heading';
  header.append(link('/explore', 'Back to Explore'));
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Across the conference';
  const title = document.createElement('h1');
  title.textContent = 'Conference Connections';
  const narrative = document.createElement('p');
  narrative.textContent = payload.conference_narrative.overall_message;
  header.append(eyebrow, title, narrative);

  const themes = document.createElement('section');
  themes.className = 'connections-section';
  const themesTitle = document.createElement('h2');
  themesTitle.textContent = 'Shared themes';
  const themeGrid = document.createElement('div');
  themeGrid.className = 'connections-grid';
  for (const theme of payload.shared_themes.slice(0, 8)) {
    const card = document.createElement('article');
    card.className = 'card connection-card';
    const heading = document.createElement('h3');
    heading.append(
      link(`/threads/${themeThreadId(theme.theme_name)}`, theme.theme_name),
    );
    const copy = document.createElement('p');
    copy.textContent = theme.synthesis;
    const talks = document.createElement('div');
    talks.className = 'connection-links';
    for (const talk of theme.talks.slice(0, 4)) {
      talks.append(link(`/read/${talk.talk_id}`, talk.speaker));
    }
    card.append(heading, copy, talks);
    themeGrid.append(card);
  }
  themes.append(themesTitle, themeGrid);

  const scriptures = document.createElement('section');
  scriptures.className = 'connections-section';
  const scriptureTitle = document.createElement('h2');
  scriptureTitle.textContent = 'Scripture chains';
  const scriptureList = document.createElement('div');
  scriptureList.className = 'connections-scriptures';
  for (const chain of payload.scripture_chains) {
    const row = document.createElement('article');
    const heading = document.createElement('h3');
    heading.append(link(scriptureHref(chain.reference), chain.reference));
    const copy = document.createElement('p');
    copy.textContent = chain.significance;
    const talks = document.createElement('div');
    talks.className = 'connection-links';
    for (const talk of chain.talks.slice(0, 3)) {
      talks.append(link(`/read/${talk.talk_id}`, talk.speaker));
    }
    row.append(heading, copy, talks);
    scriptureList.append(row);
  }
  scriptures.append(scriptureTitle, scriptureList);
  page.append(header, themes, scriptures);
  return page;
}
